import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ChooseAuthType from '../pages/auth/ChooseAuthType';
import EmailAuth from '../pages/auth/EmailAuth';
import NostrAuth from '../pages/auth/NostrAuth';
import AuthLayout from '../pages/auth/components/AuthLayout';
import OtpAuth from '../pages/auth/OtpAuth';
import SidebarMain from '../pages/chat/SidebarMain';
import MessagePage from '../pages/chat/MessagePage';
import ProfilePageUser from '../pages/chat/ProfilePageUser';
import RedirectFirstMessagePage from '../pages/chat/RedirectFirstMessagePage';
import ProfilePageGroup from '../pages/chat/ProfilePageGroup';
import ProfilePageEvent from '../pages/chat/ProfilePageEvent';
import ReceiveTransaction from '../pages/chat/wallet/ReceiveTransaction';
import SnackbarWallet from '../components/SnackbarWallet';
import TransactionDetails from '../pages/chat/wallet/TransactionDetails';
import { RedirectHandler } from '../components/RedirectHandler';
import { useAuth } from '../hooks/useAuth';
import { AuthenticationStatus, AuthenticationStatusGetStream } from '../store/slices/authSlice';
import CallPage from '../pages/chat/CallPage';
import DialogIncomingCall from '../components/DialogIncomingCall';
import DialogActiveCall from '../components/DialogActiveCall';
import DialogCallingCall from '../components/DialogCallingCall';
import { useStream } from '../hooks/useStream';
import SendTxRecipient from '../pages/chat/wallet/SendTxRecipient';
import SendTxAmount from '../pages/chat/wallet/SendTxAmount';
import SendTxRecap from '../pages/chat/wallet/SendTxRecap';
import CreateInvoiceAmount from '../pages/chat/wallet/CreateInvoiceAmount';
import CreateInvoiceRecap from '../pages/chat/wallet/CreateInvoiceRecap';
import RestoreWallet from '../pages/chat/wallet/RestoreWallet';
import ViewSeed from '../pages/chat/wallet/ViewSeed';
import PageEventParticipants from '../pages/chat/PageEventParticipants';
import { WalletActiveRoute } from '../components/WalletActiveRoute';

// Protected Route wrapper component
const ProtectedRoute = ({ children, allowedStatus }: { 
    children: React.ReactNode, 
    allowedStatus?: AuthenticationStatus[] 
}) => {
    const { status } = useAuth();

    if (!allowedStatus?.includes(status)) {
        return null;
    }

    return <>{children}</>;
};

const AppRouter: React.FC = () => {
    const { statusGetStream } = useStream();

    return (
        <BrowserRouter>
            <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<AuthLayout />}>
                    <Route index element={<ChooseAuthType />} />
                    <Route path="email" element={<EmailAuth />} />
                    <Route path="nostr" element={<NostrAuth />} />
                    <Route path="otp" element={<OtpAuth />} />
                </Route>

                {/* Protected Wallet Routes */}
                <Route path="/home/wallet" element={
                    <ProtectedRoute allowedStatus={[AuthenticationStatus.AUTHENTICATED]}>
                        <SidebarMain />
                    </ProtectedRoute>
                }>
                    <Route path="" element={null} />
                    <Route path="send/recipient" element={<WalletActiveRoute><SendTxRecipient /></WalletActiveRoute>} />
                    <Route path="send/amount" element={<WalletActiveRoute><SendTxAmount /></WalletActiveRoute>} />
                    <Route path="send/recap" element={<WalletActiveRoute><SendTxRecap /></WalletActiveRoute>} />
                    <Route path="receive" element={<WalletActiveRoute><ReceiveTransaction /></WalletActiveRoute>} />
                    <Route path="tx/:paymentHash" element={<WalletActiveRoute><TransactionDetails /></WalletActiveRoute>} />
                    <Route path="receive/create-invoice/amount" element={<WalletActiveRoute><CreateInvoiceAmount /></WalletActiveRoute>} />
                    <Route path="receive/create-invoice/recap" element={<WalletActiveRoute><CreateInvoiceRecap /></WalletActiveRoute>} />
                    <Route path="restore" element={<RestoreWallet />} />
                    <Route path="seed" element={<WalletActiveRoute><ViewSeed /></WalletActiveRoute>} />
                </Route>

                {/* Other Protected Routes */}
                <Route path="/home/:tab" element={
                    <ProtectedRoute allowedStatus={[AuthenticationStatus.AUTHENTICATED]}>
                        <SidebarMain />
                    </ProtectedRoute>
                }>
                    <Route index element={<RedirectFirstMessagePage />} />
                    <Route path="chat/:type/:id" element={<MessagePage />} />
                    <Route path="user/:userId" element={<ProfilePageUser />} />
                    <Route path="group/:groupId" element={<ProfilePageGroup channelType="group" />} />
                    <Route path="event-group/:groupId" element={<ProfilePageGroup channelType="event" />} />
                    <Route path="event/:eventId" element={<ProfilePageEvent />} />
                    <Route path="edit-event/:eventId" element={null} />
                    <Route path="event/participants/:eventId" element={<PageEventParticipants />} />
                    <Route path="call" element={<CallPage />} />
                </Route>
            </Routes>
            <SnackbarWallet />
            <RedirectHandler />
            {
                statusGetStream === AuthenticationStatusGetStream.LOGGED && (
                    <>
                        <DialogIncomingCall />
                        <DialogCallingCall />
                        <DialogActiveCall />
                    </>
                )
            }
        </BrowserRouter>
    );
};

export default AppRouter; 